/**
 * Training mode is the only switch for automatic Sikka help.
 *
 * Stored in two places, and nowhere else (there is no training-mode cookie):
 * - localStorage key from helpStateStorageKey, read synchronously
 * - HelpPreference/Load and Save (userpreferences). Load returns an empty
 *   state for anonymous users; a failed call is not the same thing.
 *
 * Before Load returns, callers use preferenceBeforeLoad. Missing or unreadable
 * storage is training mode off, so the mascot, panel, and tours do not flash.
 * While training mode is off, nothing auto-opens on the first visit, on a
 * later visit, on SPA or dynamic navigation, or from the dialog-tour runner.
 * The ? button, Ctrl+/, and Start tour (including ?helpTour=1) stay manual.
 */

export interface HelpPreferenceState {
    trainingMode: boolean;
    completedTours: string[];
}

export type HelpTourLaunch = "auto" | "manual";

export function defaultHelpPreference(): HelpPreferenceState {
    return { trainingMode: false, completedTours: [] };
}

export function helpStateStorageKey(username: string | null | undefined): string {
    return "mERP26.help.state." + (username || "");
}

export function normalizeHelpPreference(raw: unknown): HelpPreferenceState {
    const source = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
    const tours = source.completedTours ?? source.CompletedTours;
    const training = source.trainingMode ?? source.TrainingMode;
    return {
        trainingMode: !!training,
        completedTours: Array.isArray(tours) ? tours.filter((id): id is string => typeof id === "string") : []
    };
}

/** Value used for auto-start decisions until HelpPreference/Load returns. */
export function preferenceBeforeLoad(stored: string | null | undefined): HelpPreferenceState {
    if (stored == null || !String(stored).trim())
        return defaultHelpPreference();
    try {
        return normalizeHelpPreference(JSON.parse(stored));
    }
    catch {
        return defaultHelpPreference();
    }
}

/**
 * A successful Load replaces local state, including the blank payload returned
 * for an anonymous user. Null means the call failed and the local copy stands.
 */
export function applyLoadedPreference(current: HelpPreferenceState, remote: unknown | null): HelpPreferenceState {
    if (remote == null)
        return current || defaultHelpPreference();
    return normalizeHelpPreference(remote);
}

/**
 * Automatic mascot, help panel, generic grid tour, and dialog tour.
 * Manual launches (help button, shortcut, Start tour, helpTour=1) always run.
 */
export function shouldStartTour(launch: HelpTourLaunch, state: HelpPreferenceState | null | undefined): boolean {
    if (launch === "manual")
        return true;
    return !!state?.trainingMode;
}
